import React from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import reportWebVitals from './reportWebVitals';

import SoundStatusProvider from './context/SoundStatusContext';

import AuthenticatedRoute from './routes/AuthenticatedRoute';
import InstructorRoute from './routes/InstructorRoute';
import StudentRoute from './routes/StudentRoute';

import './index.css';
import Root from './components/Root';
import ErrorBoundary from './components/ErrorBoundary';

import ClassificationRouter from './components/Classification/router';
import ClassificationRouterTutorial from './components/Classification/router-tutorial';
import ClassificationIntro from './components/Classification/intro';
import ClassificationPoints from './components/Classification/points';
import ClassificationBadge from './components/Classification/badge';
import ClassificationSummary from './components/Classification/summary';
import ClassificationSummaryBreakdown from './components/Classification/summary-breakdown';
import ClassificationSummaryDecisionBreakdown from './components/Classification/summary-decision-breakdown';
import ClassificationSummaryDecisionBreakdownClassifiedMultistep from './components/Classification/summary-decision-breakdown-classified-multistep';
import ClassificationSummaryFeedback from './components/Classification/summary-feedback';
import ClassificationSummaryFinal from './components/Classification/summary-final';
import ClassificationFeedback from './components/Classification/feedback';
import ClassificationAccuracy from './components/Classification/accuracy';

import NormalRouter from './components/NormalBalance/router';
import NormalRouterTutorial from './components/NormalBalance/router-tutorial';
import NormalBalanceIntro from './components/NormalBalance/intro';
import NormalBalancePoints from './components/NormalBalance/points';
import NormalBalanceAccuracy from './components/NormalBalance/accuracy';
import NormalBalanceMostChallenging from './components/NormalBalance/most-challenging';
import NormalBalanceBadge from './components/NormalBalance/badge';
import NormalBalanceSummary from './components/NormalBalance/summary';
import NormalBalanceSummaryBreakdown from './components/NormalBalance/summary-breakdown';
import NormalSummaryDecisionBreakdown from './components/NormalBalance/summary-decision-breakdown';
import NormalSummaryFeedback from './components/NormalBalance/summary-feedback';
import NormalSummaryFinal from './components/NormalBalance/summary-final';

import ThePrinciplesRouter from './components/ThePrinciples/router';
import ThePrinciplesIntro from './components/ThePrinciples/intro';
import ThePrinciplesApcIntro from './components/ThePrinciples/apcIntro';
import ThePrinciplesTutorial from './components/ThePrinciples/tutorial';
import ThePrinciplesIntroMessages from './components/ThePrinciples/intro-messages';
import ThePrinciplesDebrief from './components/ThePrinciples/debrief';
import ThePrinciplesSummary from './components/ThePrinciples/summary';
import ThePrinciplesSummaryApc from './components/ThePrinciples/summary-apc';
import ThePrinciplesSummaryBreakdown from './components/ThePrinciples/summary-breakdown';
import ThePrinciplesSummaryDecisionBreakdown from './components/ThePrinciples/summary-decision-breakdown';
import ThePrinciplesSummaryFeedback from './components/ThePrinciples/summary-feedback';
import ThePrinciplesSummaryFinal from './components/ThePrinciples/summary-final';

import BalancedEquationRouter from './components/BalancedEquation/router';
import BalancedEquationIntro from './components/BalancedEquation/intro';
import BalancedEquationTutorial from './components/BalancedEquation/tutorial';
import BalancedEquationBadge from './components/BalancedEquation/badge';
import BalancedEquationSummary from './components/BalancedEquation/summary';
import BalancedEquationSummaryBreakdown from './components/BalancedEquation/summary-breakdown';
import BalancedEquationSummaryDecisionBreakdown from './components/BalancedEquation/summary-decision-breakdown';
import BalancedEquationSummaryDecisionBreakdownAccounts from './components/BalancedEquation/summary-decision-breakdown-accounts';
import BalancedEquationSummaryFeedback from './components/BalancedEquation/summary-feedback';
import BalancedEquationSummaryFinal from './components/BalancedEquation/summary-final';

import ReviewSession from './components/ReviewSession';

import Onboarding from './components/Onboarding';
import OnboardingPreferredName from './components/Onboarding/preferred-name';

import InstructorWelcomeToAccountingGames from './components/Instructor/welcome/welcomeToAccountingGames';
import InstructorWelcomeMasteringFundamentals from './components/Instructor/welcome/masteringFundamentals';
import InstructorWelcomeHowScoringWorks from './components/Instructor/welcome/howScoringWorks';
import InstructorWelcomeWaysToUse from './components/Instructor/welcome/waysToUse';
import InstructorWelcomeSettingUpForClass from './components/Instructor/welcome/settingUpForClass';

import InstructorOnboarding from './components/Instructor/onboarding';
import InstructorOnboardingAllSet from './components/Instructor/onboarding/all-set';
import InstructorOnboardingAllExercises from './components/UI/instructor/onboarding/allExercises';

import InstructorReviewSession from './components/Instructor/review-session';

import InstructorOnboardingReviewSession from './components/Instructor/onboarding-review-session';
import InstructorOnboardingReviewSessionAllSet from './components/Instructor/onboarding-review-session/all-set';
import InstructorOnboardingReviewSessionCompetition from './components/Instructor/onboarding-review-session/competition';
import InstructorOnboardingAssignExercise from './components/Instructor/onboarding-review-session/assign-exercise';

import InstructorSettingsAssignments from './components/Instructor/settings/assignments';
import InstructorSettingsAddAssignments from './components/Instructor/settings/assignments/add';
import InstructorSettingsEditAssignment from './components/Instructor/settings/assignments/edit';
import InstructorSettingsEditTournament from './components/Instructor/settings/assignments/edit-tournament';
import InstructorSettingsRoster from './components/Instructor/settings/roster';

import InstructorInsightsPoints from './components/Instructor/insights/leaderboard/points';
import InstructorInsightsAccuracy from './components/Instructor/insights/leaderboard/accuracy';
import InstructorInsightsAverageTime from './components/Instructor/insights/leaderboard/averageTime';

import InstructorInsightsOverall from './components/Instructor/insights/decisionInsights/overall';
import InstructorInsightsClassifyingNormal from './components/Instructor/insights/decisionInsights/classifying-normal';
import InstructorInsightsClassifiedMultistep from './components/Instructor/insights/decisionInsights/classified-multistep';
import InstructorInsightsClassification from './components/Instructor/insights/decisionInsights/classification';
import InstructorInsightsNormal from './components/Instructor/insights/decisionInsights/normal';
import InstructorInsightsBalanced from './components/Instructor/insights/decisionInsights/balanced';
import InstructorInsightsDoubleEntry from './components/Instructor/insights/decisionInsights/double-entry';
import InstructorInsightsPrinciples from './components/Instructor/insights/decisionInsights/principles';

import InstructorProgress from './components/Instructor/insights/progressScores/progress';
import InstructorScores from './components/Instructor/insights/progressScores/scores';

import Login from './components/Login';
import Transition from './components/Transition/';
import NotFound from './components/NotFound';
import Assignments from './components/Assignments';
import LoginIntro from './components/Login/intro';
import SubscriptionExpired from './components/SubscriptionExpired';

import Welcome from './components/Welcome';

import DecisionInsightsOverall from './components/Insights/decisionInsights/overall';
import DecisionInsightsClassifyingNormal from './components/Insights/decisionInsights/classifying-normal';
import DecisionInsightsDoubleEntry from './components/Insights/decisionInsights/double-entry';
import DecisionInsightsPrinciples from './components/Insights/decisionInsights/principles';

import InsightsPoints from './components/Insights/pointsScores/points';
import InsightsScores from './components/Insights/pointsScores/scores';

const queryClient = new QueryClient();

const router = createBrowserRouter([
  {
    path: '/',
    element: <Root />,
    children: [      
      {
        path: 'classification',
        children: [
          {
            path: 'level/:levelNumber',
            element: <AuthenticatedRoute element={<ClassificationRouter key={window.location.pathname} />} />
          },
          {
            path: 'level/:levelNumber/tutorial',
            element: <AuthenticatedRoute element={<ClassificationRouterTutorial />} />
          },
          {
            path: 'level/:levelNumber/points',
            element: <AuthenticatedRoute element={<ClassificationPoints />} />
          },
          {
            path: 'level/:levelNumber/intro',
            element: <AuthenticatedRoute element={<ClassificationIntro />} />
          },
          {
            path: 'badge',
            element: <AuthenticatedRoute element={<ClassificationBadge />} />
          },
          {
            path: 'summary',
            element: <AuthenticatedRoute element={<ClassificationSummary />} />
          },
          {
            path: 'summary/breakdown',
            element: <AuthenticatedRoute element={<ClassificationSummaryBreakdown />} />
          },
          {
            path: 'summary/decision-breakdown',
            element: <AuthenticatedRoute element={<ClassificationSummaryDecisionBreakdown />} />
          },
          {
            path: 'summary/decision-breakdown/classified-multistep',
            element: <AuthenticatedRoute element={<ClassificationSummaryDecisionBreakdownClassifiedMultistep />} />
          },
          {
            path: 'summary/feedback',
            element: <AuthenticatedRoute element={<ClassificationSummaryFeedback />} />
          },
          {
            path: 'summary/final',
            element: <AuthenticatedRoute element={<ClassificationSummaryFinal />} />
          },
          {
            path: 'feedback',
            element: <AuthenticatedRoute element={<ClassificationFeedback />} />
          },
          {
            path: 'accuracy',
            element: <AuthenticatedRoute element={<ClassificationAccuracy />} />
          },
        ]
      },
      {
        path: 'normal',
        children: [
          {
            path: 'level/:levelNumber',
            element: <AuthenticatedRoute element={<NormalRouter key={window.location.pathname} />} />
          },
          {
            path: 'level/:levelNumber/tutorial',
            element: <AuthenticatedRoute element={<NormalRouterTutorial />} />
          },
          {
            path: 'level/:levelNumber/points',
            element: <AuthenticatedRoute element={<NormalBalancePoints />} />
          },
          {
            path: 'level/:levelNumber/accuracy',
            element: <AuthenticatedRoute element={<NormalBalanceAccuracy />} />
          },
          {
            path: 'level/:levelNumber/most-challenging',
            element: <AuthenticatedRoute element={<NormalBalanceMostChallenging />} />
          },
          {
            path: 'level/:levelNumber/intro',
            element: <AuthenticatedRoute element={<NormalBalanceIntro />} />
          },
          {
            path: 'badge',
            element: <AuthenticatedRoute element={<NormalBalanceBadge />} />
          },
          {
            path: 'summary',
            element: <AuthenticatedRoute element={<NormalBalanceSummary />} />
          },
          {
            path: 'summary/breakdown',
            element: <AuthenticatedRoute element={<NormalBalanceSummaryBreakdown />} />
          },
          {
            path: 'summary/decision-breakdown',
            element: <AuthenticatedRoute element={<NormalSummaryDecisionBreakdown />} />
          },
          {
            path: 'summary/feedback',
            element: <AuthenticatedRoute element={<NormalSummaryFeedback />} />
          },
          {
            path: 'summary/final',
            element: <AuthenticatedRoute element={<NormalSummaryFinal />} />
          },
        ]
      },
      {
        path: '/the-principles',
        children: [
          {
            path: 'level/:levelNumber/intro',
            element: <AuthenticatedRoute element={<ThePrinciplesIntro />} />
          },
          {
            path: 'level/:levelNumber/level-intro',
            element: <AuthenticatedRoute element={<ThePrinciplesApcIntro />} />
          },
          {
            path: 'level/:levelNumber/tutorial',
            element: <AuthenticatedRoute element={<ThePrinciplesTutorial />} />
          },
          {
            path: 'intro-messages',
            element: <AuthenticatedRoute element={<ThePrinciplesIntroMessages />} />
          },
          {
            path: 'level/:levelNumber',
            element: <AuthenticatedRoute element={<ThePrinciplesRouter />} />
          },
          {
            path: 'debrief',
            element: <AuthenticatedRoute element={<ThePrinciplesDebrief />} />
          },
          {
            path: 'summary',
            element: <AuthenticatedRoute element={<ThePrinciplesSummary />} />
          },
          {
            path: 'summary/assumptions-principles-constraints',
            element: <AuthenticatedRoute element={<ThePrinciplesSummaryApc />} />
          },
          {
            path: 'summary/breakdown',
            element: <AuthenticatedRoute element={<ThePrinciplesSummaryBreakdown />} />
          },
          {
            path: 'summary/decision-breakdown',
            element: <AuthenticatedRoute element={<ThePrinciplesSummaryDecisionBreakdown />} />
          },
          {
            path: 'summary/feedback',
            element: <AuthenticatedRoute element={<ThePrinciplesSummaryFeedback />} />
          },
          {
            path: 'summary/final',
            element: <AuthenticatedRoute element={<ThePrinciplesSummaryFinal />} />
          },
        ]
      },
      {
        path: '/balanced-equation',
        children: [
          {
            path: 'level/:levelNumber/tutorial',
            element: <AuthenticatedRoute element={<BalancedEquationTutorial />} />
          },
          {
            path: 'level/:levelNumber/intro',
            element: <AuthenticatedRoute element={<BalancedEquationIntro />} />
          },
          {
            path: 'level/:levelNumber',
            element: <AuthenticatedRoute element={<BalancedEquationRouter />} />
          },
          {
            path: 'badge',
            element: <AuthenticatedRoute element={<BalancedEquationBadge />} />
          },
          {
            path: 'summary',
            element: <AuthenticatedRoute element={<BalancedEquationSummary />} />
          },
          {
            path: 'summary/breakdown',
            element: <AuthenticatedRoute element={<BalancedEquationSummaryBreakdown />} />
          },
          {
            path: 'summary/decision-breakdown',
            element: <AuthenticatedRoute element={<BalancedEquationSummaryDecisionBreakdown />} />
          },
          {
            path: 'summary/decision-breakdown-accounts',
            element: <AuthenticatedRoute element={<BalancedEquationSummaryDecisionBreakdownAccounts />} />
          },
          {
            path: 'summary/feedback',
            element: <AuthenticatedRoute element={<BalancedEquationSummaryFeedback />} />
          },
          {
            path: 'summary/final',
            element: <AuthenticatedRoute element={<BalancedEquationSummaryFinal />} />
          },
        ]
      },
      {
        path: 'review-session/:tournamentId',
        element: <AuthenticatedRoute element={<ReviewSession/>} />
      },
      {
        path: 'onboarding',
        children: [
          {
            path: '',
            element: <StudentRoute element={<Onboarding/>} />
          },
          {
            path: 'preferred-name',
            element: <StudentRoute element={<OnboardingPreferredName/>} />
          },
        ]
      },
      {
        path: 'welcome',
        element: <StudentRoute element={<Welcome/>} />
      },
      {
        path: 'instructor',
        children: [
          {
            path: 'dashboard',
            element: <InstructorRoute element={<InstructorWelcomeToAccountingGames/>} />
          },
          {
            path: 'welcome',
            children: [
              {
                path: 'welcome-to-accounting-games',
                element: <InstructorRoute element={<InstructorWelcomeToAccountingGames/>} />
              },
              {
                path: 'mastering-fundamentals',
                element: <InstructorRoute element={<InstructorWelcomeMasteringFundamentals/>} />
              },
              {
                path: 'how-scoring-works',
                element: <InstructorRoute element={<InstructorWelcomeHowScoringWorks/>} />
              },
              {
                path: 'ways-to-use',
                element: <InstructorRoute element={<InstructorWelcomeWaysToUse/>} />
              },
              {
                path: 'setting-up-for-class',
                element: <InstructorRoute element={<InstructorWelcomeSettingUpForClass/>} />
              },
            ]
          },
          {
            path: 'settings',
            children: [
              {
                path: 'assignments',
                children: [
                  {
                    path: '',
                    element: <InstructorRoute element={<InstructorSettingsAssignments />} />
                  },
                  {
                    path: 'add',
                    element: <InstructorRoute element={<InstructorSettingsAddAssignments />} />
                  }
                ]
              },
              {
                path: 'assignment/edit/:assignmentId',
                element: <InstructorRoute element={<InstructorSettingsEditAssignment />} />
              },
              {
                path: 'tournament/edit/:tournamentId',
                element: <InstructorRoute element={<InstructorSettingsEditTournament />} />
              },
              {
                path: 'tournament/new',
                element: <InstructorRoute element={<InstructorSettingsEditTournament />} />
              },
              {
                path: 'roster',
                element: <InstructorRoute element={<InstructorSettingsRoster />} />
              },
            ]
          },
          {
            path: 'insights',
            children: [
              {
                path: '',
                element: <InstructorRoute element={<InstructorInsightsPoints />} />
              },
              {
                path: 'leaderboard',
                children: [
                  {
                    path: 'points',
                    element: <InstructorRoute element={<InstructorInsightsPoints />} /> 
                  },
                  {
                    path: 'accuracy',
                    element: <InstructorRoute element={<InstructorInsightsAccuracy />} /> 
                  },
                  {
                    path: 'average-time',
                    element: <InstructorRoute element={<InstructorInsightsAverageTime />} /> 
                  }
                ]
              },
              {
                path: 'decision-insights',
                children: [
                  {
                    path: 'overall',
                    element: <InstructorRoute element={<InstructorInsightsOverall />} /> 
                  },
                  {
                    path: 'classifying-normal',
                    element: <InstructorRoute element={<InstructorInsightsClassifyingNormal />} />
                  },
                  {
                    path: 'classified-multistep',
                    element: <InstructorRoute element={<InstructorInsightsClassifiedMultistep />} />
                  },
                  {
                    path: 'double-entry',
                    element: <InstructorRoute element={<InstructorInsightsDoubleEntry />} /> 
                  },
                  {
                    path: 'principles',
                    element: <InstructorRoute element={<InstructorInsightsPrinciples />} /> 
                  },
                  {
                    path: 'classification/:levelNumber',
                    element: <InstructorRoute element={<InstructorInsightsClassification />} /> 
                  },
                  {
                    path: 'normal/:levelNumber',
                    element: <InstructorRoute element={<InstructorInsightsNormal />} /> 
                  },
                  {
                    path: 'balanced/:levelNumber',
                    element: <InstructorRoute element={<InstructorInsightsBalanced />} /> 
                  }
                ],
              },
              {
                path: 'progress-scores',
                children: [
                  {
                    path: 'progress',
                    element: <InstructorRoute element={<InstructorProgress />} /> 
                  },
                  {
                    path: 'scores',
                    element: <InstructorRoute element={<InstructorScores />} /> 
                  }
                ],
              }
            ]
          },
          {
            path: 'onboarding',
            children: [
              {
                path: '',
                element: <InstructorRoute element={<InstructorOnboarding/>} />
              },
              {
                path: 'all-set',
                element: <InstructorRoute element={<InstructorOnboardingAllSet/>} />
              },
              {
                path: 'all-exercises',
                element: <InstructorRoute element={<InstructorOnboardingAllExercises/>} />
              },
            ]
          },
          {
            path: 'onboarding-review-session',
            children: [
              {
                path: '',
                element: <InstructorRoute element={<InstructorOnboardingReviewSession/>} />
              },
              {
                path: 'all-set',
                element: <InstructorRoute element={<InstructorOnboardingReviewSessionAllSet/>} />
              },
              {
                path: 'competition',
                element: <InstructorRoute element={<InstructorOnboardingReviewSessionCompetition/>} />
              },
              {
                path: 'assign-exercise',
                element: <InstructorRoute element={<InstructorOnboardingAssignExercise/>} />
              },
            ]
          },
          {
            path: 'review-session/:tournamentId',
            element: <InstructorRoute element={<InstructorReviewSession/>} />
          }
        ]
      },
      {
        path: '/acc',
        children: [
          {
            path: 'login',
            element: <Login />
          },
          {
            path: 'welcome',
            element: <LoginIntro />
          },
          {
            path: 'transition',
            element: <AuthenticatedRoute element={<Transition/>} />
          },
          {
            path: 'subscription-expired',
            element: <AuthenticatedRoute element={<SubscriptionExpired />} />
          }
        ]
      },
      {
        path: 'assignments',
        element: <AuthenticatedRoute element={<Assignments />} />
      },
      {
        path: 'insights',
        children: [
          {
            path: '',
            element: <AuthenticatedRoute element={<DecisionInsightsOverall />} />
          },
          {
            path: 'decision-insights',
            children: [
              {
                path: 'overall',
                element: <AuthenticatedRoute element={<DecisionInsightsOverall />} /> 
              },
              {
                path: 'classifying-normal',
                element: <AuthenticatedRoute element={<DecisionInsightsClassifyingNormal />} /> 
              },
              {
                path: 'double-entry',
                element: <AuthenticatedRoute element={<DecisionInsightsDoubleEntry />} /> 
              },
              {
                path: 'principles',
                element: <AuthenticatedRoute element={<DecisionInsightsPrinciples />} /> 
              },
            ],
          },
          {
            path: 'points-scores',
            children: [
              {
                path: 'points',
                element: <AuthenticatedRoute element={<InsightsPoints />} /> 
              },
              {
                path: 'scores',
                element: <AuthenticatedRoute element={<InsightsScores />} /> 
              }
            ],
          }
        ]
      },
      {
        path: '*',
        element: <AuthenticatedRoute element={<NotFound />} />
      }
    ]
  }
], {
  future: {
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_relativeSplatPath: true,
    v7_skipActionErrorRevalidation: true,
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <DndProvider backend={HTML5Backend}>
        <QueryClientProvider client={queryClient}>
          <SoundStatusProvider>
            <RouterProvider router={router} future={{ v7_startTransition: true }} />
          </SoundStatusProvider>
        </QueryClientProvider>
      </DndProvider>
    </ErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
